import { addFieldError, addFormError, applyRequired, createResult, hasValue, runFieldMap } from "./shared.js";
import { profileFieldValidators } from "./profileFields.js";

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const mailMeValueFields = Object.freeze(["mailMeBackoffice", "mailMeGameNews", "mailMeMarketing"]);
const mailMeMuteFields = Object.freeze(["mailMeBackofficeMuteTill", "mailMeGameNewsMuteTill", "mailMeMarketingMuteTill"]);
const mailMeMuteByField = Object.freeze(Object.fromEntries(mailMeValueFields.map((field, index)=>[
    field,
    mailMeMuteFields[index]
])));


export const profileFormFields = Object.freeze({
    personal: Object.freeze(["name", "locale", "email", "phone", "country", "city", "street", "house", "zip", "birthday"]),
    character: Object.freeze(["isPublic", "alias"]),
    pin: Object.freeze(["pin", "pinConfirm"]),
    mailMe: Object.freeze([...mailMeValueFields, ...mailMeMuteFields]),
    consents: Object.freeze([
        "signedTermsAt",
        "signedPrivacyAt",
        "signedBookingAt",
        "signedAvaAt",
        "signedAvbAt",
        "signedAvcAt"
    ])
});

export const profileFormKeys = Object.freeze(Object.keys(profileFormFields));

const defaultRequiredByForm = Object.freeze({
    personal: Object.freeze([]),
    pin: Object.freeze(["pin", "pinConfirm"])
});

const toRequiredByForm = (formName, options = {}) => {
    const custom = options.requiredByForm?.[formName];
    if (Array.isArray(custom)) { return custom; }
    return defaultRequiredByForm[formName] || [];
};

const profileFormRules = Object.freeze({
    character: (values, fieldErrors) => {
        if (!values.isPublic || hasValue(values.alias) || fieldErrors.alias) { return; }
        addFieldError(fieldErrors, "alias", "required");
    },
    pin: (values, fieldErrors, formErrors) => {
        if (fieldErrors.pin || fieldErrors.pinConfirm) { return; }
        if (!hasValue(values.pin) || !hasValue(values.pinConfirm)) { return; }
        if (values.pin === values.pinConfirm) { return; }
        addFormError(formErrors, "mismatch", ["pin", "pinConfirm"]);
    },
    mailMe: values=>{
        const now = Date.now();
        for (const [field, muteField] of Object.entries(mailMeMuteByField)) {
            const muteTime = values[muteField]?.getTime?.();
            if (values[field] && Number.isFinite(muteTime) && muteTime > now) { continue; }
            values[muteField] = null;
        }
    }
});

const buildContext = (options = {}) => ({
    roles: options.roles
});

export const validateProfileForm = (formName, input = {}, options = {}) => {
    const fields = profileFormFields[formName];
    if (!fields) { throw new Error(`Unknown profile form '${formName}'`); }
    const { values, fieldErrors } = runFieldMap(input, fields, profileFieldValidators, buildContext(options));
    applyRequired(values, fieldErrors, toRequiredByForm(formName, options));
    const formErrors = [];
    const onValidate = profileFormRules[formName];
    if (onValidate) { onValidate(values, fieldErrors, formErrors, options); }
    return createResult(values, fieldErrors, formErrors);
};

const deleteFields = Object.freeze(["confirmDelete", "deletePin"]);

export const validateProfileDelete = (input = {}, options = {}) => {
    const { values, fieldErrors } = runFieldMap(input, deleteFields, profileFieldValidators, buildContext(options));
    if (!values.confirmDelete) {
        addFieldError(fieldErrors, "confirmDelete", "required_true");
    }
    if (options.requirePin && !hasValue(values.deletePin) && !fieldErrors.deletePin) {
        addFieldError(fieldErrors, "deletePin", "required");
    }
    return createResult(values, fieldErrors, []);
};

const editableFormNames = Object.freeze(["personal", "character", "pin", "mailMe"]);

export const validateEditableProfilePatch = (input = {}, options = {}) => {
    const values = {};
    const fieldErrors = {};
    const formErrors = [];

    for (const formName of editableFormNames) {
        const formFields = profileFormFields[formName];
        const partialInput = {};
        let hasAnyField = false;

        for (const fieldName of formFields) {
            if (!hasOwn(input, fieldName)) { continue; }
            partialInput[fieldName] = input[fieldName];
            hasAnyField = true;
        }
        if (!hasAnyField) { continue; }

        const result = validateProfileForm(formName, partialInput, options);
        Object.assign(values, result.values);
        Object.assign(fieldErrors, result.fieldErrors);
        formErrors.push(...result.formErrors);
    }

    return createResult(values, fieldErrors, formErrors);
};
